import type { ICategory } from '@/types/category';
import type { IClaim, IClaimSubmission, IUploadUrlResponse } from '@/types/claim';

import { apiClient } from '@/utils/apiClient';

export interface FetchCategoriesResponse {
  items: ICategory[];
  nextCursor: string | null;
}

export const fetchCategories = async (): Promise<FetchCategoriesResponse> => {
  const { data } = await apiClient.get<FetchCategoriesResponse>('/categories');
  return data;
};

export interface FetchClaimsParams {
  limit?: number;
  exclusiveStartKey?: string | null;
  user?: string | null;
}

export interface FetchClaimsResponse {
  items: IClaim[];
  nextCursor: string | null;
}

export const fetchClaims = async (params?: FetchClaimsParams): Promise<FetchClaimsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.exclusiveStartKey) {
    queryParams.append('exclusiveStartKey', encodeURIComponent(params.exclusiveStartKey));
  }
  if (params?.user) {
    queryParams.append('user', params.user);
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/claims?${queryString}` : '/claims';

  const { data } = await apiClient.get<FetchClaimsResponse>(url);
  return data;
};

export const requestUploadUrl = async (
  fileName: string,
  fileType: string,
): Promise<IUploadUrlResponse> => {
  const { data } = await apiClient.post<IUploadUrlResponse>('/claims/upload-url', {
    fileName,
    fileType,
  });
  return data;
};

export const uploadToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }
};

export const submitClaim = async (
  claimData: IClaimSubmission,
): Promise<{ success: boolean; message: string }> => {
  const { data } = await apiClient.post('/claims/submit', claimData);
  return data;
};

export interface DeleteClaimResponse {
  success: boolean;
  message: string;
  claim?: IClaim | null;
}

export const deleteClaim = async (claimId: string): Promise<DeleteClaimResponse> => {
  const encodedId = encodeURIComponent(claimId);
  const { data } = await apiClient.put<DeleteClaimResponse>(`/claims/${encodedId}/delete`);
  return data;
};

export interface UpdateClaimPayload {
  category: number;
  amount: number;
  details: string;
  receiptS3Key?: string;
  receiptS3Keys?: string[];
}

export interface UpdateClaimResponse {
  success: boolean;
  message: string;
  claim?: IClaim | null;
  /** Present when cross-month edit replaced the transaction with a new TN# id */
  newClaimId?: string;
  replacedClaimId?: string;
}

export const updateClaim = async (
  claimId: string,
  payload: UpdateClaimPayload,
): Promise<UpdateClaimResponse> => {
  const encodedId = encodeURIComponent(claimId);
  const { data } = await apiClient.patch<UpdateClaimResponse>(`/claims/${encodedId}`, payload);
  return data;
};

export interface ReceiptDownloadResponse {
  downloadUrl: string;
  expiresIn: number;
}

export const getClaimReceiptDownloadUrl = async (
  claimId: string,
  s3Key: string,
): Promise<ReceiptDownloadResponse> => {
  const encodedId = encodeURIComponent(claimId);
  const encodedKey = encodeURIComponent(s3Key);
  const { data } = await apiClient.get<ReceiptDownloadResponse>(
    `/claims/${encodedId}/receipt-download?s3Key=${encodedKey}`,
  );
  return data;
};
